// Nhóm theo ngày cho trang Lịch sử — dùng chung cho CẢ hai tab (Luận giải và
// Đàm đạo). Hai tab nhóm lệch nhau (một bên "Hôm qua", một bên "Cũ hơn" cho cùng một
// mốc) đọc ra là dữ liệu bị lạc chỗ, nên phép suy diễn phải nằm ở một chỗ duy nhất.

export type HistoryGroupKey = "today" | "yesterday" | "older";

export const HISTORY_GROUP_LABELS: Record<HistoryGroupKey, string> = {
  today: "Hôm nay",
  yesterday: "Hôm qua",
  older: "Cũ hơn",
};

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat("vi-VN", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function isSameLocalDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

export function historyGroupOf(createdAt: string): HistoryGroupKey {
  const date = new Date(createdAt);
  // Mốc không đọc được rơi về "Cũ hơn" thay vì dựng thêm một nhóm thứ tư: một dòng
  // lệch định dạng không đáng làm hỏng cấu trúc cả danh sách.
  if (Number.isNaN(date.getTime())) return "older";

  const today = new Date();
  if (isSameLocalDay(date, today)) return "today";

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (isSameLocalDay(date, yesterday)) return "yesterday";

  return "older";
}

export function formatHistoryDate(createdAt: string): string {
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? "Không rõ thời gian" : DATE_TIME_FORMATTER.format(date);
}

export type HistoryGroup<T> = {
  key: HistoryGroupKey;
  label: string;
  items: T[];
};

/**
 * Chia danh sách ĐÃ SẮP XẾP thành các nhóm ngày.
 *
 * ⚠️ Chỉ nhóm, KHÔNG sắp xếp lại — thứ tự trong mỗi nhóm giữ nguyên thứ tự đầu vào.
 * Nhóm rỗng bị bỏ hẳn: một tiêu đề "Hôm qua" trống đọc ra là dữ liệu bị mất.
 */
export function groupByDay<T>(
  items: T[],
  getDate: (item: T) => string,
  sort: "newest" | "oldest",
): HistoryGroup<T>[] {
  const buckets: Record<HistoryGroupKey, T[]> = { today: [], yesterday: [], older: [] };
  items.forEach((item) => {
    buckets[historyGroupOf(getDate(item))].push(item);
  });

  const order: HistoryGroupKey[] =
    sort === "newest" ? ["today", "yesterday", "older"] : ["older", "yesterday", "today"];

  return order
    .map((key) => ({ key, label: HISTORY_GROUP_LABELS[key], items: buckets[key] }))
    .filter((group) => group.items.length > 0);
}
